<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin"></div>
    </Transition>

    <v-form :name="formProps.formName"
            @keyup.enter="autoSave = false">
        <Dialog modal
                :visible="isPreviewMailModalOpen"
                v-model:visible="isPreviewMailModalOpen"
                :header="__('Email preview')"
                :style="{ width: '60%' }">
            <PreviewMailForm v-model:isPreviewMailModalOpen="isPreviewMailModalOpen"
                             :formProps="mail">
            </PreviewMailForm>
        </Dialog>

        <ConfirmDialog header="Confirm operation"
                       question="Do you want to send mailings to workers now?"
                       v-model="isConfirmationOpen"
                       :operation="sendMail" />

        <TabView scrollable
                 style="overflow: visible"
                 :pt="{ panelContainer: { class: 'form-tab-panel' } }">
            <TabPanel :header="__('Basic data')"
                      headerClass="form-tab-header">
                <div class="grid">
                    <FormInputText fieldName="event"
                                   label="Event"
                                   size="10"
                                   validation="required"
                                   :isFormDisabled="isFormDisabledForNonAdmin"
                                   :isFocus="true"
                                   v-model:field="crud.event" />
                    <FormInputToggleButton fieldName="is_active"
                                           label="Is mailing active?"
                                           size="2"
                                           :isFormDisabled="isFormDisabled"
                                           v-model:field="crud.is_active" />

                </div>
                <div class="grid">
                    <FormInputText fieldName="description"
                                   label="Description"
                                   :isFormDisabled="isFormDisabled"
                                   v-model:field="crud.description" />
                </div>

                <div class="grid">
                    <FormInputText fieldName="subject"
                                   label="Subject"
                                   validation="required"
                                   :isFormDisabled="isFormDisabled"
                                   v-model:field="crud.subject" />
                </div>
                <div class="grid">
                    <FormEditor fieldName="body"
                                label="Body"
                                toolbar="large"
                                :editorStyle="{ height: '220px' }"
                                :isFormDisabled="isFormDisabled"
                                v-model:field="crud.body" />
                </div>

            </TabPanel>

            <TabPanel v-if="crud.log"
                      :header="__('Changelog')"
                      headerClass="form-tab-header">
                <ChangeLog :log="crud?.log" />
            </TabPanel>

        </TabView>

        <div class="grid">
            <FormButtons :formProps="props.formProps"
                         :sourceForm="crud"
                         :validation="validation"
                         :beforeEdit="setDisableFieldForNonAdmin"
                         :buttonsVisibility="buttonsVisibility"
                         v-model:isFormDisabled="isFormDisabled"
                         v-model:autoSave="autoSave">
                <template #after-in-show="buttonProps">
                    <CustomButton v-if="crud?.event?.startsWith('hand') && crud.is_active"
                                  name="sendMail"
                                  :buttonProps="buttonProps"
                                  :buttonsVisibility="buttonsVisibility"
                                  @click.stop="openConfirmationWindow()"
                                  style="margin:0 5px"
                                  severity="primary">
                        {{ __('Send') }}
                    </CustomButton>
                    <CustomButton v-if="crud?.event?.startsWith('hand')"
                                  name="sendMail"
                                  :buttonProps="buttonProps"
                                  :buttonsVisibility="buttonsVisibility"
                                  @click.stop="mailPreview()"
                                  style="margin:0 5px; background-color:aliceblue; border-color:lightblue; color:black">
                        {{ __('Preview') }}
                    </CustomButton>
                </template>
            </FormButtons>
        </div>
    </v-form>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import { validation, initForm } from '@pages/Helper/crud_utils.js'
import FormInputText from '@crud/FormInputText.vue'
import FormEditor from '@crud/FormEditor.vue'
import FormInputToggleButton from '@crud/FormInputToggleButton.vue'
import FormButtons from '@crud/FormButtons.vue'
import CustomButton from '@crud/CustomButton.vue'
import ConfirmDialog from '@crud/ConfirmDialog.vue'
import PreviewMailForm from './PreviewMailForm.vue'
import Dialog from 'primevue/dialog';
import ChangeLog from '@crud/ChangeLog.vue'
import { __, getStoreValue, is } from '@helper/utils.js'
import { getFromRoute, screenMessage, getTimeouts } from '../../../Helper/utils';

// -----------------------------------------------------

const props = defineProps({
    formProps: Object,
})

const isLoading = ref(true);
const isFormDisabled = ref(false)
const autoSave = ref(false)
const isFormDisabledForNonAdmin = ref(false)
const buttonsVisibility = ref(null);
const isConfirmationOpen = ref(false)
const isPreviewMailModalOpen = ref(false)

const crud = ref({})
const mail = ref({})

// -----------------------------------------------------

onMounted(async () => {
    let superAdminRole = getStoreValue('superAdminRole')
    if (props.formProps.editMode === true || props.formProps.addMode === true) {
        isFormDisabledForNonAdmin.value = !is(superAdminRole)
    } else {
        isFormDisabledForNonAdmin.value = true
    }

    await initForm(props, crud, isLoading, null, null, buttonsVisibility)
})

// -----------------------------------------------------

const setDisableFieldForNonAdmin = () => {
    let superAdminRole = getStoreValue('superAdminRole')
    isFormDisabledForNonAdmin.value = !is(superAdminRole)
}

// -----------------------------------------------------

const sendMail = async () => {
    isLoading.value = true

    const event = crud?.value?.event
    const timeouts = getTimeouts()
    const response = await getFromRoute('mailing-send-event', { event: event }, 'post')

    screenMessage({ message: response.message, title: "Information", type: response.status == 'success' ? "info" : "error", position: "center", timeout: timeouts.error });
    isLoading.value = false
}

// -----------------------------------------------------

const openConfirmationWindow = () => {
    isConfirmationOpen.value = true
}

// -----------------------------------------------------

const mailPreview = async () => {
    isLoading.value = true
    const event = crud?.value?.event
    const timeouts = getTimeouts()
    const response = await getFromRoute('mailing-preview-event', { event: event }, 'post')

    if (response && typeof response === 'object' && response.status !== 'error') {
        mail.value = { ...response }
        isLoading.value = false
        isPreviewMailModalOpen.value = true
    } else {
        isLoading.value = false
        screenMessage({ message: response.message, title: "Information", type: "error", position: "center", timeout: timeouts.error });
        console.error(__('Invalid response for mail preview'), response);
    }
}

// -----------------------------------------------------

</script>

<style lang="scss" scoped></style>
